import { Channel, type InitDataQuery } from '#graphql-operations'
import type { NuxtSvgSpriteSymbol } from '#nuxt-svg-icon-sprite/runtime'
import type { InitData } from '~/composables/useInitData/types'
import { notNullish } from '@vueuse/core'
import { getPathFromUrl } from '~/helpers'

function getChannelIcon(channel: Channel): NuxtSvgSpriteSymbol {
  if (channel === Channel.business) {
    return 'business'
  }

  return 'sight_seeing'
}

function extractNodeReference(data?: {
  url?: { path?: string }
}): string | null {
  if (data && data.url && data.url.path) {
    return data.url.path
  }

  return null
}

function mapInitData(data?: InitDataQuery): InitData {
  if (!data) {
    throw new Error('Failed to load init data.')
  }

  return {
    activeDomain: data?.activeDomain,
    availableChannels: (data?.availableChannels || [])
      .map((v) => {
        if (v && v.label && v.landingPage?.url?.path && v.enum) {
          const translations = (v.landingPage.translations || []).reduce<
            Record<string, string>
          >((acc, translation) => {
            if (translation && translation.url?.path && translation.langcode) {
              acc[translation.langcode] = translation.url.path
            }
            return acc
          }, {})
          return {
            enum: v.enum,
            label: v.label,
            landingPage: getPathFromUrl(v.landingPage.url.path),
            icon: getChannelIcon(v.enum),
            translations,
          }
        }

        return null
      })
      .filter(notNullish),
    pages: {
      eventCalendar: extractNodeReference(data.globalConfig?.eventCalendarPage),
      zurichCardInfo: extractNodeReference(data.globalConfig?.zurichCardPage),
    },
    featureflags: (data.featureFlags || []).filter(notNullish),
  }
}

export default defineNuxtPlugin({
  name: 'zt:init-data',
  dependsOn: ['zt:graphql-middleware'],
  async setup() {
    const language = useCurrentLanguage()
    const initData = useState<InitData | null>('initData', () => null)

    if (import.meta.server) {
      const event = useRequestEvent()
      const { value: cachedValue, addToCache } = await useDataCache<InitData>(
        'initData_' + language.value,
        event,
      )

      // We got a cached query we can use.
      if (cachedValue) {
        initData.value = cachedValue
        return
      }

      const response = await useGraphqlQuery('initData', null, {
        clientContext: {
          l: language.value,
        },
        graphqlCaching: {
          client: true,
        },
      })

      const mapped = mapInitData(response.data)
      await addToCache(
        mapped,
        response.extensions?.cacheability?.tagsNuxt,
        60 * 60 * 12,
      )

      initData.value = mapped
      return
    }

    if (import.meta.client) {
      async function loadInitData(langcode: string) {
        const response = await useGraphqlQuery('initData', null, {
          graphqlCaching: {
            client: true,
          },
          clientContext: {
            l: langcode,
          },
        })

        initData.value = mapInitData(response.data)
      }

      // Setup a watcher on the client to reload the data when the language changes.
      watch(language, function (newLanguage) {
        loadInitData(newLanguage)
      })

      // Data is already fetched, so we can return.
      // This is the case during hydration after SSR.
      if (initData.value !== null) {
        return
      }

      await loadInitData(language.value)
    }
  },
})
